import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, User, Tag, ChevronRight, Search, ArrowRight } from 'lucide-react';
import { LogoSmall } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { buildPreview } from '../utils/stripHtml';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default images for posts without featured images - varied for visual appeal
const DEFAULT_BLOG_IMAGES = [
  'https://images.unsplash.com/photo-1678755363068-6ba249ed21cd?w=600', // Freeze dryer/kitchen appliance
  'https://images.unsplash.com/photo-1693940747726-aff55aa0db30?w=600', // Dried peaches/fruits
  'https://images.unsplash.com/photo-1707225883695-2822ac16ad2c?w=600', // Freeze dried fruits close up
  'https://images.unsplash.com/photo-1587377224458-872ac425416c?w=600', // Food preservation container
  'https://images.unsplash.com/photo-1643494847705-74808059bf07?w=600', // Food storage
  'https://images.unsplash.com/photo-1576867757603-05b134ebc379?w=600', // Dried foods variety
];

// Get a consistent image based on post ID/index
const getDefaultImage = (postId, index = 0) => {
  // Use post ID to get consistent image, or fall back to index
  const hash = postId ? postId.charCodeAt(0) + postId.charCodeAt(postId.length - 1) : index;
  return DEFAULT_BLOG_IMAGES[hash % DEFAULT_BLOG_IMAGES.length];
};

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const selectedCategory = searchParams.get('category');
  const selectedTag = searchParams.get('tag');

  useEffect(() => {
    fetchData();
  }, [selectedCategory, selectedTag]);

  const fetchData = async () => {
    try {
      let url = `${API_URL}/api/blog/posts?published_only=true`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (selectedTag) url += `&tag=${selectedTag}`;
      
      const [postsRes, categoriesRes, tagsRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API_URL}/api/blog/categories`),
        axios.get(`${API_URL}/api/blog/tags`)
      ]);
      
      // API returns array directly, not wrapped in { posts: [...] }
      const postsData = Array.isArray(postsRes.data) ? postsRes.data : (postsRes.data.posts || []);
      const tagsData = Array.isArray(tagsRes.data) ? tagsRes.data : (tagsRes.data.tags || []);
      
      setPosts(postsData);
      setCategories(categoriesRes.data || []);
      setTags(tagsData);
      
      // Find featured post
      const featured = postsData.find(p => p.featured);
      setFeaturedPost(featured || null);
    } catch (e) {
      console.error('Error fetching blog data:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    return post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const regularPosts = filteredPosts.filter(p => !p.featured || p.id !== featuredPost?.id);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      <div className="pt-36 pb-16 section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-5xl font-bold text-[#0a0203] mb-4" style={{  }}>
              D-Freeze Blog
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tips, recipes, and guides for freeze drying. Plus news about our products and partners.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-12 py-3 rounded-xl bg-white"
                data-testid="blog-search"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link to="/blog">
                <Button 
                  variant={!selectedCategory && !selectedTag ? 'default' : 'outline'}
                  className={!selectedCategory && !selectedTag ? 'bg-[#003456]' : 'border-gray-300'}
                >
                  All
                </Button>
              </Link>
              {categories.map(cat => (
                <Link key={cat.id} to={`/blog?category=${cat.id}`}>
                  <Button 
                    variant={selectedCategory === cat.id ? 'default' : 'outline'}
                    className={selectedCategory === cat.id ? 'bg-[#003456]' : 'border-gray-300'}
                  >
                    {cat.name}
                  </Button>
                </Link>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#003456] border-t-transparent"></div>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featuredPost && !selectedCategory && !selectedTag && !searchQuery && (
                <Link to={`/blog/${featuredPost.slug}`} className="block mb-12 group">
                  <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="aspect-video md:aspect-[4/3] overflow-hidden">
                        <img 
                          src={featuredPost.featured_image || getDefaultImage(featuredPost.id)} 
                          alt={featuredPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-8 md:p-12 flex flex-col justify-center bg-gradient-to-br from-white to-gray-50">
                        <span className="inline-block px-4 py-1.5 bg-[#003456] text-white text-xs font-semibold rounded-full mb-4 w-fit uppercase tracking-wider">
                          Featured Article
                        </span>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0a0203] mb-4 group-hover:text-[#003456] transition-colors leading-tight">
                          {featuredPost.title}
                        </h2>
                        <p className="text-gray-600 mb-6 line-clamp-3 text-base md:text-lg leading-relaxed">
                          {buildPreview(featuredPost, 220)}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          {featuredPost.author && (
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {featuredPost.author.name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {formatDate(featuredPost.created_at)}
                          </span>
                        </div>
                        <div className="mt-6">
                          <span className="inline-flex items-center text-[#003456] font-semibold group-hover:underline">
                            Read Full Article <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Posts Grid */}
              {regularPosts.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {regularPosts.map((post, index) => (
                    <Link key={post.id} to={`/blog/${post.slug}`} className="group" data-testid={`blog-post-${post.id}`}>
                      <article className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                        <div className="aspect-video overflow-hidden relative">
                          <img 
                            src={post.featured_image || getDefaultImage(post.id, index)} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {post.category && (
                            <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm text-[#003456] text-xs font-semibold rounded-full shadow-sm">
                              {post.category.name}
                            </span>
                          )}
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                          <h3 className="text-xl font-bold text-[#0a0203] mb-3 group-hover:text-[#003456] transition-colors line-clamp-2 leading-tight">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1 leading-relaxed">
                            {buildPreview(post, 160)}
                          </p>
                          <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {formatDate(post.created_at)}
                            </span>
                            <span className="text-[#003456] font-semibold flex items-center gap-1 group-hover:underline">
                              Read More <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </span>
                          </div>
                        </div>
                        
                        {/* Partner Promotion Badge */}
                        {post.partner_promotion && (
                          <div className="absolute top-3 right-3 px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-medium rounded">
                            Partner
                          </div>
                        )}
                      </article>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-white rounded-2xl">
                  <h3 className="text-xl font-semibold text-[#0a0203] mb-2">No articles found</h3>
                  <p className="text-gray-500 mb-6">Check back soon for new content!</p>
                  <Link to="/blog">
                    <Button variant="outline" className="border-[#003456] text-[#003456]">
                      View All Posts
                    </Button>
                  </Link>
                </div>
              )}

              {/* Tags Cloud */}
              {tags.length > 0 && (
                <div className="mt-16 bg-white rounded-2xl p-8">
                  <h3 className="text-lg font-semibold text-[#0a0203] mb-4" style={{  }}>
                    Popular Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                      <Link 
                        key={tag} 
                        to={`/blog?tag=${encodeURIComponent(tag)}`}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          selectedTag === tag 
                            ? 'bg-[#003456] text-white' 
                            : 'bg-[#F8FAFC] text-gray-700 hover:bg-[#dbeef5]'
                        }`}
                      >
                        <Tag className="h-3 w-3 inline mr-1" />
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Newsletter CTA */}
          <div className="mt-16 bg-[#003456] rounded-3xl p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{  }}>
              Subscribe to Our Newsletter
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Get the latest freeze drying tips, recipes, and exclusive offers delivered to your inbox.
            </p>
            <NewsletterForm />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Newsletter Form Component
const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className="bg-white/10 rounded-xl p-4 max-w-md mx-auto">
        <p className="text-white font-medium">Thanks for subscribing! Check your inbox soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        className="flex-1 bg-white text-[#0a0203]"
        required
        data-testid="newsletter-email"
      />
      <Button 
        type="submit" 
        disabled={loading}
        className="bg-white text-[#003456] hover:bg-gray-100"
        data-testid="newsletter-submit"
      >
        {loading ? 'Subscribing...' : 'Subscribe'}
      </Button>
    </form>
  );
};

export default Blog;
